import { Dialog, Transition } from "@headlessui/react";
import { useTranslation } from "@sg/i18n";
import { Badge, Logo } from "@sg/ui";
import { IconMenu2, IconX } from "@tabler/icons-react";
import clsx from "clsx";
import { Fragment, ReactNode, useState } from "react";
import { isStagingEnvironment } from "~/util/environment";
import { SidebarItem } from "./SidebarItem";
import { SidebarUserMenu } from "./SidebarUserMenu";
import { useSidebarItems } from "./useSidebarItems";

interface SidebarProps {
  hidden?: boolean;
  children: ReactNode;
}

export function Sidebar({ hidden, children }: SidebarProps) {
  const { t } = useTranslation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { items: navigation, name } = useSidebarItems();

  return (
    <>
      <div>
        {!hidden && (
          <>
            <Transition.Root show={sidebarOpen} as={Fragment}>
              <Dialog as="div" className="relative z-50 lg:hidden" onClose={setSidebarOpen}>
                <Transition.Child
                  as={Fragment}
                  enter="transition-opacity ease-linear duration-300"
                  enterFrom="opacity-0"
                  enterTo="opacity-100"
                  leave="transition-opacity ease-linear duration-300"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <div className="fixed inset-0 bg-zinc-900/80 backdrop-blur-sm" />
                </Transition.Child>

                <div className="fixed inset-0 flex">
                  <Transition.Child
                    as={Fragment}
                    enter="transition ease-in-out duration-300 transform"
                    enterFrom="-translate-x-full"
                    enterTo="translate-x-0"
                    leave="transition ease-in-out duration-300 transform"
                    leaveFrom="translate-x-0"
                    leaveTo="-translate-x-full"
                  >
                    <Dialog.Panel className="relative mr-16 flex w-full max-w-xs flex-1">
                      <Transition.Child
                        as={Fragment}
                        enter="ease-in-out duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in-out duration-300"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                      >
                        <div className="absolute left-full top-0 flex w-16 justify-center pt-5">
                          <button type="button" className="-m-2.5 p-2.5" onClick={() => setSidebarOpen(false)}>
                            <span className="sr-only">Close sidebar</span>
                            <IconX className="h-6 w-6 text-white" aria-hidden="true" />
                          </button>
                        </div>
                      </Transition.Child>
                      <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-gradient-card px-6 pb-2 dark:ring-white/10">
                        <div className="flex h-16 shrink-0 items-center">
                          <a href="https://swapglobal.com/">
                            <div className="flex items-center gap-x-4">
                              <span className="sr-only">SwapGlobal</span>
                              <Logo className="w-8 h-8" />
                            </div>
                          </a>
                        </div>
                        <nav className="flex flex-1 flex-col">
                          <ul role="list" className="flex flex-1 flex-col gap-y-7">
                            <li>
                              <ul role="list" className="-mx-2 space-y-5">
                                <li>
                                  <SidebarItem name={name} />
                                </li>
                                {navigation.map((item) => (
                                  <li key={item.name}>
                                    <SidebarItem {...item} />
                                  </li>
                                ))}
                              </ul>
                            </li>
                          </ul>
                        </nav>
                      </div>
                    </Dialog.Panel>
                  </Transition.Child>
                </div>
              </Dialog>
            </Transition.Root>

            {/* Static sidebar for desktop */}
            <div className="hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:w-72 lg:flex-col p-5">
              <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-gradient-card px-6 rounded-xl">
                <div className="flex h-16 shrink-0 items-center">
                  <a href="https://swapglobal.com/">
                    <div className="flex items-center gap-x-4 relative">
                      <span className="sr-only">SwapGlobal</span>
                      <Logo className="w-8 h-8" />
                      <img src="/logo.svg" className="-ml-2 text-[20px] h-11" alt="SwapGlobal" />
                      {isStagingEnvironment() && (
                        <Badge variant="outline" className="left-full top-6 -ml-4 text-[10px] uppercase absolute">
                          {t("demo")}
                        </Badge>
                      )}
                    </div>
                  </a>
                </div>
                <nav className="flex flex-1 flex-col">
                  <ul role="list" className="flex flex-1 flex-col gap-y-7">
                    <li>
                      <ul role="list" className="-mx-2 space-y-5">
                        <li>
                          <SidebarItem name={name} />
                        </li>
                        {navigation.map((item) => (
                          <li key={item.name}>
                            <SidebarItem {...item} />
                          </li>
                        ))}
                      </ul>
                    </li>
                    <li className="-mx-6 mt-auto">
                      <SidebarUserMenu />
                    </li>
                  </ul>
                </nav>
              </div>
            </div>

            <div className="sticky top-0 z-20 flex items-center gap-x-6 bg-gradient-card px-4 py-4 shadow-sm sm:px-6 lg:hidden">
              <button
                type="button"
                className="-m-2.5 p-2.5 text-zinc-700 dark:text-zinc-400 lg:hidden"
                onClick={() => setSidebarOpen(true)}
              >
                <span className="sr-only">Open sidebar</span>
                <IconMenu2 className="h-6 w-6" aria-hidden="true" />
              </button>

              <div className="flex-1 text-sm font-semibold leading-6 text-card dark:text-white">
                {t("product_name")}
              </div>
              <SidebarUserMenu />
            </div>
          </>
        )}

        <main className={clsx(!hidden && "py-4 lg:pl-72")}>
          <div className={clsx(!hidden && "px-4 sm:px-6")}>{children}</div>
        </main>
      </div>
    </>
  );
}
